/**
 * How the session list sorts (SessionsViewer): by any column, up or down, with the newest
 * session first by default. A row with no value for the column (no end time, no version)
 * always goes last, whichever way the column is sorted; ties keep the newest first.
 *
 * The choice lives in the address (?sort=dur&order=asc), so Back from a session returns to
 * the list as it was sorted. The default leaves the address clean.
 */

export const DEFAULT_SORT = { key: 'start', dir: 'desc' };

// Status: what needs a look first, then what is done.
const STATUS_RANK = { live: 0, pending: 1, incomplete: 2, failed: 3, complete: 4 };

const text = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/** key → [the row's value or null, compare two values, the direction a first click picks] */
const KEYS = {
    start: [(r) => r.startMs ?? null, (a, b) => a - b, 'desc'],
    dur: [(r) => r.durMs ?? null, (a, b) => a - b, 'desc'],
    game: [(r) => r.game || null, text, 'asc'],
    who: [(r) => r.who || null, text, 'asc'],
    // numeric: 1.0.1504 after 1.0.99, 0.10 after 0.9
    ver: [(r) => (r.ver && r.ver !== '—' ? r.ver : null), text, 'desc'],
    status: [(r) => (r.live ? STATUS_RANK.live : r.status ? STATUS_RANK[r.status] ?? 5 : null), (a, b) => a - b, 'asc'],
};

export const SORT_KEYS = Object.keys(KEYS);

// own keys only: "constructor" or "__proto__" from an address are not columns
const known = (key) => typeof key === 'string' && Object.prototype.hasOwnProperty.call(KEYS, key);

/** The direction a column takes when it is first chosen: newest, longest and latest first; names A–Z. */
export function firstDir(key) {
    return known(key) ? KEYS[key][2] : 'asc';
}

/** A header click: the same column turns around, another column starts in its own first direction. */
export function nextSort(sort, key) {
    if (sort.key === key) return { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
    return { key, dir: firstDir(key) };
}

/** The sort the address asks for, or the default when it names nothing this list can sort by. */
export function readSort(params, allowed = SORT_KEYS) {
    const key = params.get('sort');
    if (!known(key) || !allowed.includes(key)) return DEFAULT_SORT;
    const order = params.get('order');
    return { key, dir: order === 'asc' || order === 'desc' ? order : firstDir(key) };
}

/** The address for a sort: the default is no parameters at all. Other parameters are kept. */
export function writeSort(params, sort) {
    const next = new URLSearchParams(params);
    if (sort.key === DEFAULT_SORT.key && sort.dir === DEFAULT_SORT.dir) {
        next.delete('sort');
        next.delete('order');
    } else {
        next.set('sort', sort.key);
        next.set('order', sort.dir);
    }
    return next;
}

/** A sorted copy of the rows. */
export function sortRows(rows, sort) {
    const [value, compare] = KEYS[known(sort.key) ? sort.key : DEFAULT_SORT.key];
    const sign = sort.dir === 'asc' ? 1 : -1;
    const newest = (a, b) => (b.startMs ?? -Infinity) - (a.startMs ?? -Infinity);
    return rows
        .map((row) => ({ row, v: value(row) }))
        .sort((a, b) => {
            if (a.v == null || b.v == null) {
                if (a.v != null) return -1;
                if (b.v != null) return 1;
                return newest(a.row, b.row);
            }
            return sign * compare(a.v, b.v) || newest(a.row, b.row);
        })
        .map((x) => x.row);
}

/** The phone's sort menu (rows are cards there, with no headers to click). */
export function sortOptions(withPlayer) {
    return [
        { value: 'start:desc', label: 'Newest first' },
        { value: 'start:asc', label: 'Oldest first' },
        { value: 'dur:desc', label: 'Longest first' },
        { value: 'dur:asc', label: 'Shortest first' },
        { value: 'game:asc', label: 'Game A–Z' },
        { value: 'game:desc', label: 'Game Z–A' },
        ...(withPlayer ? [{ value: 'who:asc', label: 'Player A–Z' }, { value: 'who:desc', label: 'Player Z–A' }] : []),
        { value: 'ver:desc', label: 'Latest version first' },
        { value: 'ver:asc', label: 'Earliest version first' },
        { value: 'status:asc', label: 'Live and pending first' },
        { value: 'status:desc', label: 'Complete first' },
    ];
}
